import React, {useState} from 'react';
import {
    Image,
    Modal,
    StyleSheet,
    Text, TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import {SCREEN_HEIGHT, SCREEN_WIDTH, WINDOW_WIDTH} from "../../../Config/Constants";
import {BORDER_COLOR, DATA_COLOR, HEADER_TITLE_COLOR, PRIMARY_BACKGROUND_COLOR} from "../../../Config/Colors";
import SaveIcon from "../../../Images/Icons/save.png";
import CloseIcon from "../../../Images/Icons/close.png";
import Dropdown from "./Dropdown";
import PowerClient from "../../../Domain/Models/PowerClient";
import Logger from "../../../Domain/Logger/Logger";
import PowerClientService from "../../../Domain/Services/PowerClientService";
import {MODAL_INFO_MESSAGE_MILLISECONDS} from "../../../Config/Parameters";

const logger = Logger.Create("EditPowerClientModal");
let timer: NodeJS.Timeout | undefined;

/**
 * The Edit Power Client Modal Props.
 */
interface EditPowerClientModalProps
{
    /**
     * Flag to toggle the modal.
     */
    show?: boolean;

    /**
     * The selected power client.
     */
    selectedClient: PowerClient;

    /**
     * Callback when the close button was pressed.
     */
    onCloseButtonSelected?: (shouldUpdate: boolean) => void;
}

/**
 * Renders the Edit Power Client Modal.
 */
export default function EditPowerClientModal({
    show,
    selectedClient,
    onCloseButtonSelected,
}: EditPowerClientModalProps): JSX.Element
{
    const [clientName, setClientName] = useState<string | null>(null);
    const [finalClientName, setFinalClientName] = useState<string | null>(null);
    const [proxy, setProxy] = useState<string | null>(null);
    const [shouldUpdate, setShouldUpdate] = useState(false);
    const [enableNameUpdate, setEnableNameUpdate] = useState(true);
    const [enableProxyUpdate, setEnableProxyUpdate] = useState(true);
    const [clientNameMessage, setClientNameMessage] = useState("");
    
    const onTextUpdated = (text: string): void =>
    {
        setClientName(text);    
    };

    const onProxyChanged = (proxyId: string): void =>
    {
        setProxy(proxyId);
    }
    
    const onUpdateProxyButtonSelected = async (): Promise<void> =>
    {
        if (proxy === null)
        {
            updateMessage("Not updating the proxy because it hasn't changed.");
            return;
        }

        logger.info(`Updating the proxy to ${proxy}`);

        setEnableNameUpdate(false);

        const result = await PowerClientService.updateClientProxy(selectedClient, proxy, finalClientName);
        updateMessage(result.message);

        setEnableNameUpdate(true);

        // Set this flag so when the modal is closed,
        // we can tell the main screen to update.
        setShouldUpdate(true);
    }
    
    const onUpdateClientNameButtonSelected = async (): Promise<void> =>
    {
        if (clientName === null)
        {
            updateMessage("Not updating the client name because it hasn't changed.");
            return;
        }

        logger.info(`Updating the client name to ${clientName}`);

        setEnableNameUpdate(false);

        const result = await PowerClientService.updateClientName(selectedClient, clientName);
        updateMessage(result.message);
        setEnableNameUpdate(true);

        if (result.success)
        {
            setFinalClientName(clientName);
        }
        
        // Set this flag so when the modal is closed,
        // we can tell the main screen to update.
        setShouldUpdate(true);
    }
    
    const onCloseButtonPressed = (): void =>
    {
        if (onCloseButtonSelected !== undefined)
        {
            onCloseButtonSelected(shouldUpdate);
        }
    }
    
    const updateMessage = (message: string): void =>
    {
        if (timer !== undefined)
        {
            clearTimeout(timer);
            timer = undefined;
        }

        setClientNameMessage(message);
        
        timer = setTimeout(() =>
        {
            setClientNameMessage("");
        }, MODAL_INFO_MESSAGE_MILLISECONDS);
    }
    
    const onModalShown = (): void =>
    {
        setClientName(null);
        setClientNameMessage("");
        setFinalClientName(null);
    }
    
    return (
        <Modal
            animationType="fade"
            visible={show}
            transparent
            statusBarTranslucent
            onShow={onModalShown}
        >
            <View style={styles.modal}>
                <View style={styles.modalContentParent}>
                    <View style={styles.sectionTitleView}>
                        <Text style={styles.titleText}>
                            Edit Power Client
                        </Text>

                        <TouchableOpacity
                            style={styles.closeButton}
                            onPress={onCloseButtonPressed}
                        >
                            <Image
                                source={CloseIcon}
                                resizeMode={"contain"}
                                style={styles.icon}
                            />
                        </TouchableOpacity>
                    </View>

                    <View style={styles.editClientNameView}>
                        <Text style={styles.labelText}>
                            Client Name
                        </Text>
                        
                        <TextInput
                            placeholder={selectedClient.name}
                            style={styles.input}
                            onChangeText={onTextUpdated}
                            placeholderTextColor={DATA_COLOR}
                        />
                    </View>

                    <View style={styles.updateClientNameButtonView}>
                        <TouchableOpacity
                            disabled={!enableNameUpdate}
                            style={[styles.saveClientNameButton, {
                                opacity: enableNameUpdate ? 1 : 0.5,
                            }]}
                            onPress={onUpdateClientNameButtonSelected}
                        >
                            <Image
                                source={SaveIcon}
                                resizeMode={"contain"}
                                style={styles.icon}
                            />

                            <Text style={styles.updateButtonText}>
                                Update Client Name
                            </Text>
                        </TouchableOpacity>
                    </View>
                    
                    <View style={styles.divider} />
                        
                    <Dropdown
                        defaultProxy={selectedClient.powerProxy}
                        onItemSelected={onProxyChanged}
                    />

                    <View style={styles.updateProxyButtonView}>
                        <TouchableOpacity
                            onPress={onUpdateProxyButtonSelected}
                            disabled={!enableProxyUpdate}
                            style={[styles.saveClientNameButton, {
                                opacity: enableProxyUpdate ? 1 : 0.5,
                            }]}
                        >
                            <Image
                                source={SaveIcon}
                                resizeMode={"contain"}
                                style={styles.icon}
                            />

                            <Text style={styles.updateButtonText}>
                                Update Selected Proxy
                            </Text>
                        </TouchableOpacity>
                    </View>

                    <View style={styles.resultView}>
                        <Text style={styles.resultText}>
                            {clientNameMessage}
                        </Text>
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    modal: {
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },
    modalContentParent: {
        width: '45%',
        height: '70%',
        backgroundColor: PRIMARY_BACKGROUND_COLOR,
        borderWidth: 2,
        borderColor: BORDER_COLOR,
        borderRadius: SCREEN_WIDTH * 0.01,
    },
    sectionTitleView: {
        flex: 0.15,
        justifyContent: 'flex-start',
        borderBottomWidth: 2,
        borderBottomColor: BORDER_COLOR,
    },
    titleText: {
        color: HEADER_TITLE_COLOR,
        fontSize: WINDOW_WIDTH * 0.02,
        fontWeight: "600",
        textAlign: 'left',
        paddingLeft: '5%',
        paddingTop: '5%',
    },
    closeButton: {
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        alignSelf: 'flex-end',
        marginRight: '2%',
        height: '40%',
        position: 'absolute',
        right: 0,
        top: '14%',
        width: undefined,
        aspectRatio: 1,
        borderRadius: SCREEN_HEIGHT,
        backgroundColor: HEADER_TITLE_COLOR,
    },
    editClientNameView: {
        flex: 0.2,
        flexDirection: 'row',
    },
    updateClientNameButtonView: {
        flex: 0.1,
        justifyContent: 'center',
        alignItems: 'flex-end',
    },
    updateProxyButtonView: {
        flex: 0.1,
        justifyContent: 'center',
        alignItems: 'flex-end',
    },
    updateButtonText: {
        textAlign: 'center',
        alignSelf: 'center',
        fontSize: SCREEN_WIDTH * 0.014,
        color: PRIMARY_BACKGROUND_COLOR,
        fontWeight: '600',
    },
    labelText: {
        color: HEADER_TITLE_COLOR,
        fontSize: WINDOW_WIDTH * 0.013,
        fontWeight: "600",
        textAlign: 'left',
        marginLeft: '5%',
        alignSelf: 'center',
        flex: 0.25,
    },
    input: {
        flex: 0.7,
        height: '50%',
        width: '50%',
        alignSelf: 'center',
        textAlign: 'left',
        fontSize: SCREEN_WIDTH * 0.015,
        color: HEADER_TITLE_COLOR,
        textShadowRadius: 10,
        paddingLeft: '3%',
        borderWidth: SCREEN_HEIGHT * 0.002,
        borderColor: BORDER_COLOR,
        borderRadius: SCREEN_WIDTH * 0.005,
        justifyContent: 'center',
    },
    saveClientNameButton: {
        width: '60%',
        alignSelf: 'center',
        height: '60%',
        borderRadius: SCREEN_WIDTH * 0.005,
        backgroundColor: HEADER_TITLE_COLOR,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
    },
    icon: {
        width: undefined,
        height: '40%',
        tintColor: PRIMARY_BACKGROUND_COLOR,
        aspectRatio: 1,
        marginRight: '5%',
    },
    divider: {
        flex: 0.05,
        justifyContent: 'center',
        borderBottomWidth: 2,
        borderBottomColor: BORDER_COLOR,
    },
    resultView: {
        flex: 0.01,
        position: 'absolute',
        bottom: '10%',
        alignSelf: 'center',
        width: '100%',
    },
    resultText: {
        textAlign: 'center',
        alignSelf: 'center',
        fontSize: SCREEN_WIDTH * 0.012,
        color: BORDER_COLOR,
        fontWeight: '600',
        width: '90%',
    },
});
